import { ResizableGrid } from '@/components/resizable-grid';
import { ReportTileContainer } from '../ReportTileContainer';
import React, { useCallback, useMemo, useState } from 'react';
import { ILayoutItem, IReportTile, TileType } from '../../types';
import { Box, Flex } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_SERVICE } from '../../api/api';
import { REPORT_GRID_CONFIG, syncLayoutWithTiles } from './helpers';
import { useShowToast } from '@/hooks';
import { DeleteConfirmationModal } from '@/componentsV1/Modal/DeleteConfirmationModal';
import { isEqual } from 'lodash';
import { FlowReportLayout } from './FlowReportLayout';
import { useKFlags } from '@/hooks/use-k-flags';

interface StaticFlowTileWrapperProps {
  tile: IReportTile;
  report: any;
  isReadOnly?: boolean;
  layout: { w: number; h: number };
  isStaticReport?: boolean;
  viewMode?: boolean;
  handleTileAction: (tileId: string, payload: any) => void;
}

const StaticTableTileWrapper = React.memo(
  ({
    tile,
    report,
    isReadOnly,
    layout,
    isStaticReport,
    viewMode,
    handleTileAction,
  }: StaticFlowTileWrapperProps) => {
    return (
      <ReportTileContainer
        tile={tile}
        report={report}
        isReadOnly={isReadOnly}
        isBlockHovering={true}
        isStaticReport={isStaticReport}
        layout={layout}
        viewMode={viewMode}
        handleTileAction={handleTileAction}
      />
    );
  },
  () => true
);

StaticTableTileWrapper.displayName = 'StaticTableTileWrapper';

// Type definition for API response
interface CustomReport {
  id: number;
  layout: {
    data: ILayoutItem[];
  };
  tiles: IReportTile[];
}

interface Props {
  customReport: CustomReport;
  isReadOnly?: boolean;
  isPrintView?: boolean;
  isStaticReport?: boolean;
  viewMode?: boolean;
  isEditMode?: boolean;
  // these are for ReportsV3
  onLayoutChange?: (newLayout: any[]) => void;
  onRemoveBlock?: (blockId: string) => void;
  onTileMetaChange?: (tileId: string, metadata: any) => void;
  getCurrentMergedData?: () => any;
}

// Type for normalized layout items used in comparison
type NormalizedLayoutItem = Pick<ILayoutItem, 'key' | 'x' | 'y' | 'w' | 'h'>;

// Normalize layout data for comparison by keeping only relevant properties
const normalizeLayoutForComparison = (
  layout: ILayoutItem[] | undefined
): NormalizedLayoutItem[] => {
  if (!layout) return [];
  return layout.map(
    ({ key, x, y, w, h }): NormalizedLayoutItem => ({ key, x, y, w, h })
  );
};

// Compare two layouts to check if they are different
const areLayoutsDifferent = (
  layout1: ILayoutItem[] | undefined,
  layout2: ILayoutItem[] | undefined
): boolean => {
  if (!layout1 || !layout2) return true;

  const normalizedLayout1 = normalizeLayoutForComparison(layout1);
  const normalizedLayout2 = normalizeLayoutForComparison(layout2);

  return !isEqual(normalizedLayout1, normalizedLayout2);
};

const MemoizedReportTileContainer = React.memo(
  ReportTileContainer,
  (prevProps, nextProps) => {
    // Compare props to prevent re-renders
    const prevIsFlow = ['flow_user_activity', 'flow_health'].includes(
      prevProps.tile.subject_type
    );
    const nextIsFlow = ['flow_user_activity', 'flow_health'].includes(
      nextProps.tile.subject_type
    );

    return (
      prevProps.tile.id === nextProps.tile.id &&
      prevProps.tile.subject_type === nextProps.tile.subject_type &&
      !prevProps.isReadOnly === !nextProps.isReadOnly &&
      isEqual(prevProps.layout, nextProps.layout) &&
      prevProps.isBlockHovering === nextProps.isBlockHovering &&
      prevProps.isStaticReport === nextProps.isStaticReport &&
      prevProps.viewMode === nextProps.viewMode &&
      prevProps.isEditMode === nextProps.isEditMode &&
      prevProps.report?.id === nextProps.report?.id &&
      prevProps.handleTileAction === nextProps.handleTileAction &&
      prevProps.onRemoveBlock === nextProps.onRemoveBlock &&
      prevIsFlow === nextIsFlow &&
      prevProps.isPrintView === nextProps.isPrintView
    );
  }
);

// Main layout component for custom report tiles
export const CustomReportTilesLayout = ({
  customReport,
  isPrintView = false,
  isReadOnly = false,
  isStaticReport = false,
  viewMode = false,
  isEditMode = false,
  onLayoutChange,
  onRemoveBlock,
  onTileMetaChange,
  getCurrentMergedData,
}: Props) => {
  const { layout, tiles } = customReport || {};
  const { data: savedLayout } = layout || {};
  const [selectedTiles, setSelectedTiles] = useState<string[]>([]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const { feReportsV3 } = useKFlags();

  const toast = useShowToast();
  const queryClient = useQueryClient();

  // Mutation to update layout changes
  const layoutMutation = useMutation<CustomReport, Error, any>({
    mutationFn: ({ reportID, payload }) =>
      API_SERVICE.updateCustomReport(reportID, payload),
    onSuccess: () => {
      toast({ title: 'Layout updated successfully' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to update the layout',
        description: error?.message || 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
    // onSettled: () => {
    //   // Refetch to ensure data consistency
    //   queryClient.invalidateQueries({
    //     queryKey: [`getCustomReports/${customReport?.id}`],
    //   });
    // },
  });

  const deleteTilesMutation = useMutation({
    mutationFn: (data) =>
      API_SERVICE.deleteTiles(data['reportID'], data['payload']),
    onSuccess: () => {
      setSelectedTiles([]);
      queryClient.invalidateQueries({
        queryKey: [`getCustomReports/${customReport['id']}`],
      });
      setShowDeleteConfirmation(false);
      toast({ title: 'Blocks deleted' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete the blocks',
        description: error.message,
        status: 'error',
      });
    },
  });

  const handleTileActionChange = useCallback((tileId: string, payload: any) => {
    if (payload?.action?.status) {
      setSelectedTiles((prev) => [...prev, tileId]);
    } else {
      setSelectedTiles((prev) => prev.filter((id) => id !== tileId));
    }
  }, []);

  const initiateDeleteTiles = () => {
    if (onRemoveBlock && feReportsV3) {
      selectedTiles.forEach((tileId) => {
        onRemoveBlock(tileId);
      });
      setSelectedTiles([]);
      setShowDeleteConfirmation(false);
    } else {
      // Use original API approach for existing functionality
      deleteTilesMutation.mutate({
        reportID: customReport?.id,
        payload: { ids: selectedTiles },
      } as any);
    }
  };

  // Prepare layout data with render functions - memoized more aggressively
  const layoutData = useMemo(() => {
    let initialLayout = syncLayoutWithTiles(
      tiles,
      savedLayout || [],
      isStaticReport
    );

    return initialLayout
      .map((layoutItem) => {
        const tile = tiles.find((t) => t.id.toString() === layoutItem.key);
        if (!tile) return null;

        const defaultSize =
          REPORT_GRID_CONFIG.TILE_SIZES[
            tile.metadata.componentType as TileType
          ] || REPORT_GRID_CONFIG.TILE_SIZES[TileType.CARD];
        return {
          ...defaultSize,
          ...layoutItem,
          render: ({ isHovering }) => {
            return (
              <MemoizedReportTileContainer
                key={`tile-${tile.id}`}
                tile={tile}
                report={customReport}
                isReadOnly={isReadOnly}
                isPrintView={isPrintView}
                isBlockHovering={feReportsV3 ? true : isHovering}
                isStaticReport={isStaticReport}
                layout={{ w: layoutItem.w, h: layoutItem.h }}
                viewMode={viewMode}
                handleTileAction={handleTileActionChange}
                onRemoveBlock={
                  onRemoveBlock && feReportsV3 ? onRemoveBlock : undefined
                }
                onTileMetaChange={onTileMetaChange}
                isEditMode={isEditMode}
                getCurrentMergedData={getCurrentMergedData}
              />
            );
          },
        };
      })
      .filter(Boolean);
  }, [
    tiles,
    savedLayout,
    customReport,
    isReadOnly,
    isStaticReport,
    viewMode,
    isEditMode,
    handleTileActionChange,
    onRemoveBlock,
    feReportsV3,
    onTileMetaChange,
    isPrintView,
    getCurrentMergedData,
  ]);

  // Handle layout changes from grid
  const handleLayoutChange = useCallback(
    (newLayout: ILayoutItem[]) => {
      if (!customReport.id) {
        return;
      }

      // Validate layout items against available tiles
      const invalidTiles = newLayout.filter(
        (layout) => !tiles.some((tile) => tile.id.toString() === layout.key)
      );

      if (invalidTiles.length > 0) {
        return;
      }

      // Process and sanitize layout data
      const sanitizedLayout = newLayout
        .map(({ key, x, y, w, h }) => {
          const tile = tiles.find((t) => t.id.toString() === key);
          if (!tile) return null;

          const tileType = tile?.metadata.componentType as TileType;
          const defaultSize =
            REPORT_GRID_CONFIG.TILE_SIZES[tileType] ||
            REPORT_GRID_CONFIG.TILE_SIZES[TileType.CARD];

          // Apply layout constraints based on tile type
          return {
            key,
            x,
            y,
            w,
            h,
            minW: defaultSize.minW,
            minH: defaultSize.minH,
            maxW: defaultSize.maxW,
            maxH: defaultSize.maxH,
          };
        })
        .filter(Boolean);

      // Update layout only if changes detected
      const layoutChanged = areLayoutsDifferent(sanitizedLayout, savedLayout);
      if (layoutChanged) {
        if (feReportsV3 && onLayoutChange) {
          // For local updates, just store in ref - no state updates needed
          onLayoutChange(sanitizedLayout);
        } else {
          // For API calls, make immediate call (existing behavior)
          layoutMutation.mutate({
            reportID: customReport.id,
            payload: { layout: { data: sanitizedLayout } },
          });
        }
      }
    },
    [
      customReport.id,
      layoutMutation,
      tiles,
      savedLayout,
      feReportsV3,
      onLayoutChange,
    ]
  );

  // Filter out table components from grid data for static reports - memoized
  const gridLayoutData = useMemo(() => {
    if (!isStaticReport) return layoutData;

    // Check if this is a flow report
    const isFlowReport = tiles.some((tile) =>
      ['flow_user_activity', 'flow_health'].includes(tile.subject_type)
    );

    if (isFlowReport && !isPrintView) {
      // For flow reports, only include charts in the grid
      return layoutData.filter((item) => {
        const tile = tiles.find((t) => t.id.toString() === item.key);
        return tile && tile.metadata.componentType === 'chart';
      });
    }

    // For non-flow reports, exclude tables from grid
    return isPrintView
      ? layoutData
      : layoutData.filter((item) => {
          const tile = tiles.find((t) => t.id.toString() === item.key);
          return tile && tile.metadata.componentType !== 'table';
        });
  }, [isStaticReport, layoutData, tiles, isPrintView]);

  // Check if there are any non-table components to display in the grid
  const hasNonTableComponents = useMemo(() => {
    if (!isStaticReport) return true;

    const isFlowReport = tiles.some((tile) =>
      ['flow_user_activity', 'flow_health'].includes(tile.subject_type)
    );

    return tiles.some(
      (tile) => tile.metadata.componentType !== 'table' && !isFlowReport
    );
  }, [tiles, isStaticReport]);

  // Check if this is a flow report
  const isFlowReport = useMemo(() => {
    return tiles.some((tile) =>
      ['flow_user_activity', 'flow_health'].includes(tile.subject_type)
    );
  }, [tiles]);

  const renderStaticTables = () => {
    const initialLayout = syncLayoutWithTiles(
      tiles,
      savedLayout || [],
      isStaticReport
    );

    // Determine which components to render
    let componentsToRender = [];

    componentsToRender = initialLayout
      .filter((layoutItem) => {
        const tile = tiles.find((t) => t.id.toString() === layoutItem.key);
        if (isFlowReport) {
          return tile && tile.metadata.componentType === 'table';
        }
        return tile && tile.metadata.componentType === 'table';
      })
      .map((layoutItem) => {
        const tile = tiles.find((t) => t.id.toString() === layoutItem.key);
        return tile ? { tile, layoutItem } : null;
      })
      .filter(Boolean);

    if (componentsToRender.length === 0) return null;

    // Render the components
    return (
      <Flex
        flexDirection="column"
        gap="14px"
        width="100%"
        pt={hasNonTableComponents ? '12px' : '0'}
      >
        {componentsToRender.map(({ tile, layoutItem }) => {
          // Create a stable key for the component
          const stableKey = `static-${tile.id}-${layoutItem.key}`;

          return (
            <Box
              key={stableKey}
              width="100%"
              minHeight={`${
                layoutItem.h * REPORT_GRID_CONFIG.GRID.ROW_HEIGHT
              }px`}
              border="1px solid"
              borderColor="border-color"
              borderRadius="sm"
              padding="16px"
              backgroundColor="white"
              boxShadow="sm"
              display="flex"
              flexDirection="column"
              position="relative"
            >
              <StaticTableTileWrapper
                tile={tile}
                report={customReport}
                isReadOnly={isReadOnly}
                isStaticReport={isStaticReport}
                layout={{ w: layoutItem.w, h: layoutItem.h }}
                viewMode={viewMode}
                handleTileAction={handleTileActionChange}
              />
            </Box>
          );
        })}
      </Flex>
    );
  };

  const showEditModeActions = feReportsV3 ? isEditMode : true;

  return (
    <Box
      className="cr-grid-container"
      height={feReportsV3 ? 'auto' : '100%'}
      overflow={feReportsV3 ? 'unset' : 'auto'}
      position="relative"
      flex="1"
    >
      {/* For flow reports with isStaticReport, use ordered rendering */}
      {isStaticReport && isFlowReport && !isPrintView ? (
        <FlowReportLayout
          tiles={tiles}
          savedLayout={savedLayout}
          customReport={customReport}
          isReadOnly={isReadOnly}
          isStaticReport={isStaticReport}
          viewMode={viewMode}
          handleTileAction={handleTileActionChange}
        />
      ) : (
        <>
          {/* Original logic for non-flow reports */}
          {
            <Box height={isStaticReport ? 'auto' : '100%'}>
              <ResizableGrid
                columns={REPORT_GRID_CONFIG.GRID.COLUMNS}
                rowHeight={REPORT_GRID_CONFIG.GRID.ROW_HEIGHT}
                config={gridLayoutData}
                onLayoutChange={handleLayoutChange}
                isPrintView={isPrintView}
                reactGridLayoutProps={{
                  isDraggable:
                    !isStaticReport && !isReadOnly && showEditModeActions,
                  isDroppable:
                    !isStaticReport && !isReadOnly && showEditModeActions,
                  isResizable:
                    !isStaticReport && !isReadOnly && showEditModeActions,
                }}
                itemProps={{
                  showBoxShadow:
                    !isStaticReport && !isReadOnly && showEditModeActions,
                  showDragHandle:
                    !isStaticReport && !isReadOnly && showEditModeActions,
                  showBorder:
                    !isStaticReport && !isReadOnly && showEditModeActions,
                }}
                showGridAlways={feReportsV3 && isEditMode}
              />
            </Box>
          }
          {/* Render static table components after the grid */}
          {isStaticReport && !isPrintView && renderStaticTables()}
        </>
      )}
      {showDeleteConfirmation && (
        <DeleteConfirmationModal
          title="Are you sure you want to delete the selected blocks?"
          subTitle="You will always be able to add it again from the block gallery"
          onClose={() => setShowDeleteConfirmation(false)}
          onSubmit={initiateDeleteTiles}
          submitButtonLabel="Delete"
          submitButtonIsLoading={deleteTilesMutation.isPending}
          showDeleteInput={false}
        />
      )}
    </Box>
  );
};
